import {persistentStore, runtimeStore, transfersStore} from '@/composables/store-accessor'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/transfers/transfers-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {PRODUCT_NAME} from '@/utils/transfers/transfers-const'
import {onmessage} from '@/utils/worker-helpers'
import {newSearch as _newSearch, partialSearch} from '@/utils/store-helpers'
import {mapHotels} from '~/utils/hotels/hotel-helpers'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'

async function loadPoint(point: any, searchRequest: any) {
    try {
        let hotelName, place: string, cityId, latitude, longitude, placeId
        if (point === 'arrivalPoint') {
            hotelName = 'arrivalHotelName'
            place = 'arrivalPlace'
            cityId = 'arrivalCityId'
            latitude = 'arrivalLatitude'
            longitude = 'arrivalLongitude'
            placeId = 'arrivalPlaceId'
        } else {
            hotelName = 'departureHotelName'
            place = 'departurePlace'
            cityId = 'departureCityId'
            latitude = 'departureLatitude'
            longitude = 'departureLongitude'
            placeId = 'departurePlaceId'
        }
        if (searchRequest[hotelName]) {
            const {hotels} = await appInstance.$api.suggestHotels.get({
                pattern: searchRequest[hotelName],
                locationId: searchRequest[cityId],
                limit: 1,
            })
            const pointInstance = mapHotels(hotels)[0]
            const {
                hotelDescriptionInfo: {latitude, longitude, address},
            } = await appInstance.$api.hotelInfo.get({hotelId: pointInstance.id})
            Object.assign(pointInstance, {latitude, longitude, address: address?.addressLine})
            return {[point]: pointInstance}
        } else if (searchRequest[place] === 'hotel') {
            const rq = {
                id: searchRequest[cityId],
                limitCities: 1,
            }
            const rs = await appInstance.$api.locations.get(rq)
            return {[point]: rs.cities[0]}
        } else if (!isNaN(searchRequest[place])) {
            const [
                trainStations,
                {
                    cities: [city],
                },
                cruisePorts,
            ] = await Promise.all([
                appInstance.$api.trainStations.get({
                    pattern: null,
                    limit: 0,
                    productType: 'TRANSFER',
                    locationId: searchRequest[cityId],
                }),
                appInstance.$api.locations.get({
                    id: searchRequest[cityId],
                    limitCities: 1,
                }),
                appInstance.$api.cruisePorts.get({
                    locationId: searchRequest[cityId],
                    limit: 0,
                }),
            ])
            const trainStation = trainStations.find(
                (trainStation: any) => trainStation.id === parseInt(searchRequest[place], 10)
            )
            const cruisePort = cruisePorts.find((port: any) => port.id === parseInt(searchRequest[place], 10))

            if (trainStation) {
                Object.assign(trainStation, {
                    type: 'trainStation',
                    cityName: city.name,
                    countryName: city.countryName,
                    parentName: city.countryName,
                    countryId: city.countryId,
                })

                return {[point]: trainStation}
            }

            if (cruisePort) {
                Object.assign(cruisePort, {
                    type: 'cruisePort',
                    cityName: city.name,
                    countryName: city.countryName,
                    parentName: city.countryName,
                    countryId: city.countryId,
                })
                return {[point]: cruisePort}
            }
        } else if (searchRequest[place] === 'position') {
            const rqCity = {
                id: searchRequest[cityId],
                limitCities: 1,
            }
            const rsCity = await appInstance.$api.locations.get(rqCity)
            const city = rsCity.cities[0]

            const rsPoint = await appInstance.$api.geocode.get({place_id: searchRequest[placeId]})
            const placeGeo = rsPoint?.results[0]

            return {
                [point]: {
                    type: 'position',
                    address: placeGeo.formatted_address,
                    latitude: +searchRequest[latitude],
                    longitude: +searchRequest[longitude],
                    city: city,
                    title: placeGeo.formatted_address,
                    placeId: searchRequest[placeId],
                    postalCode: placeGeo.address_components.find(i => i.types.includes('postal_code'))?.long_name,
                },
            }
        } else {
            const rq = {
                iataCode: searchRequest[place],
                limit: 1,
            }
            // if (isStoreDataPersist(cookie, rq)) return
            const rs = await appInstance.$api.airports.get(rq)
            //TODO refactor with autocomplete
            rs.forEach((airport: any) => {
                Object.assign(airport, {parentName: airport.countryName})
            })
            return {[point]: rs[0]}
        }
    } catch (e) {
        return {[point]: {}}
    }
}

export const useTransfersRuntimeStore = defineStore('transfersRuntime', () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {
        searchActiveCount,
        partialSearchTimeoutInstance,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        SET_PARTIAL_SEARCH_RESULTS_CTS,
        SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
        STOP_FILTER,
        stopSearch,
        filterActive,
    } = productRuntimeStore

    const departurePoint = ref({})
    const arrivalPoint = ref({})

    function SET_DEPARTURE_POINT(val: any) {
        departurePoint.value = val
    }

    function SET_ARRIVAL_POINT(val: any) {
        arrivalPoint.value = val
    }

    function RESET() {
        searchActiveCount.value = 0
    }

    async function loadDeparturePoint(searchRequest: any) {
        departurePoint.value = (<any>await loadPoint('departurePoint', searchRequest))?.departurePoint || {}
    }

    async function loadArrivalPoint(searchRequest: any) {
        arrivalPoint.value = (<any>await loadPoint('arrivalPoint', searchRequest))?.arrivalPoint || {}
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        onmessage(PRODUCT_NAME, STOP_FILTER, filterActive, SET_OFFERS)
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        transfersStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    function reset() {
        _newSearch(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), transfersStore)
        RESET()
        transfersStore.RESET()
    }

    function newSearch() {
        _newSearch(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), transfersStore)
    }

    // TODO [P3] Need to check this on change locale
    async function reload() {
        const promises = []
        promises.push(loadDeparturePoint(transfersStore.searchRequest))
        promises.push(loadArrivalPoint(transfersStore.searchRequest))

        await Promise.all(promises)
    }

    async function search(rq: any) {
        START_SEARCH()
        _newSearch(stopSearch, SET_OFFERS, rq, searchResponseBlank(), transfersStore)
        EventBus.$emit(SEARCH_EVENT)
        try {
            const {arrivalHotelName, departureHotelName, ...searchRequest} = rq
            await partialSearch(
                SET_PARTIAL_SEARCH_RESULTS_CTS,
                SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
                partialSearchTimeoutInstance,
                searchRequest,
                PRODUCT_NAME,
                transfersStore,
                appInstance.$api.searchTransfer
            )
        } finally {
            STOP_SEARCH()
        }
    }

    const searchPageLink = computed(() => {
        return (searchRequest: any) => {
            const {partialResponse, convertToCurrency, ...query} = searchRequest
            return {name: 'transfers', query}
        }
    })

    const infoPageLink = computed(() => {
        return (product: any, searchRequest: any) => {
            const {query} = searchPageLink.value(searchRequest)
            if (product.productId) query.productId = product.productId
            query.name = product.info.name
            return {name: 'transfer', query}
        }
    })

    const ownProduct = computed(() => {
        return (product: any) => runtimeStore.ownProduct(product?.info?.supplierCode)
    })

    const offerMainImage = computed(() => {
        return (offer: any, info: any) => {
            if (!info.images || !info.images.length) return {url: null}
            let mainImage = info.images.find((image: any) => image.mainImage) || {url: null}
            if (!mainImage.url && info.images.length > 0) {
                mainImage = info.images[0]
            }
            if (mainImage.linkedEntityName && mainImage.linkedEntityName !== offer.info.description) {
                mainImage =
                    info.images.find((image: any) => image.linkedEntityName === offer.info.description) || mainImage
            }
            return mainImage
        }
    })

    const searchRequestFromQuery = computed(() => {
        return (query: any) => {
            //TODO Need filter $route.query params
            const rq = Object.assign(searchRequestBlank(), query)
            if (typeof rq.childrenAges === 'string') {
                rq.childrenAges = [parseInt(rq.childrenAges, 10)]
            } else {
                rq.childrenAges = rq.childrenAges.map((age: string) => parseInt(age, 10))
            }
            rq.convertToCurrency = persistentStore.currency
            rq.adults = parseInt(rq.adults, 10)
            rq.departureCityId = parseInt(rq.departureCityId, 10)
            rq.departureCountryId = parseInt(rq.departureCountryId, 10)
            rq.arrivalCityId = parseInt(rq.arrivalCityId, 10)
            rq.arrivalCountryId = parseInt(rq.arrivalCountryId, 10)
            rq.privateTransfer = rq.privateTransfer === 'true' || rq.privateTransfer === true
            rq.groupTransfer = rq.groupTransfer === 'true' || rq.groupTransfer === true
            if (rq.citizenshipId) {
                rq.citizenshipId = parseInt(rq.citizenshipId, 10)
            }
            delete rq.name
            delete rq.productId
            return rq
        }
    })

    return {
        ...productRuntimeStore,
        departurePoint,
        arrivalPoint,
        SET_DEPARTURE_POINT,
        SET_ARRIVAL_POINT,
        RESET,
        loadDeparturePoint,
        loadArrivalPoint,
        clientInit,
        changePrice,
        reset,
        newSearch,
        reload,
        search,
        searchPageLink,
        infoPageLink,
        ownProduct,
        offerMainImage,
        searchRequestFromQuery,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useTransfersRuntimeStore, import.meta.hot))
